import type { ReactNode } from 'react'
import InfoTip from './InfoTip'
import { fill, COMPONENTS_SECRET_INPUT_TEXTS as T } from '../../shared/texts'

// One stored secret in a form: the masked input, what is known about the stored value, and —
// when `onClear` is given — the only way to actually REMOVE it.
//
// Why removing needs its own control at all: everywhere in this app an empty secret field
// means „leave the stored one alone". That rule is what keeps a half-filled form from wiping
// a live key, so it must not be softened — and it left the mask able to overwrite a secret
// but never to take one away. Until this existed, a test key put into the production
// settings could not be got rid of from the UI at all.
//
// Every secret field goes through this component so the two behaviours can never drift apart
// per field: an empty field keeps, the button removes. `hint` may be empty (some fields store
// no last-four); then the label just says „gesetzt".

interface Props {
    label: string
    // Required, not optional: a nameless <input> is exactly the shape the autofill guard
    // exists to keep out (e2e/autofill.spec.ts). A browser that finds no name of its own
    // falls back to guessing from the surroundings — and next to an e-mail field it guesses
    // wrong. `type="password"` does not exempt it: a password manager offers the account
    // password there just as happily, and what belongs in these fields is an API key or an
    // author's IBAN, never the person sitting in front of the screen.
    name: string
    isSet: boolean
    hint?: string
    value: string
    onChange: (v: string) => void
    placeholder?: string
    disabled?: boolean
    info?: ReactNode
    // Present → a „Entfernen" button appears while a secret is stored. The parent asks for
    // confirmation and does the call; this component never deletes on its own.
    onClear?: () => void
}

export default function SecretInput({
    label, name, isSet, hint, value, onChange, placeholder, disabled, info, onClear,
}: Props) {
    return (
        <div className="field">
            <label>
                {label}
                {isSet && <span className="hint">{fill(T.isSet, { hint: hint ? fill(T.isSetHint, { hint }) : '' })}</span>}
                {info && <> <InfoTip>{info}</InfoTip></>}
            </label>
            {/* Not a plain .row: inside a flex row an <input> shrinks to its default size, and
                the secret fields would end up narrower than every ordinary field next to them.
                .secret-input lets the input take the space and keeps the button at its own. */}
            <div className="secret-input">
                <input
                    type="password" name={name} autoComplete="off" disabled={disabled}
                    placeholder={isSet ? T.keepPlaceholder : placeholder}
                    value={value} onChange={(e) => onChange(e.target.value)}
                />
                {isSet && onClear && (
                    <button
                        type="button" className="secondary" disabled={disabled} onClick={onClear}
                        title={T.clearTitle}
                    >
                        {T.clear}
                    </button>
                )}
            </div>
        </div>
    )
}
